/**
 * Owner notifications (BACKLOG P5 web part; docs/API.md "Notifications"): the header bell's unread count stays
 * fresh through `GET /me/notifications/stream` (SSE, `event: notification`). Each event bumps the cached count
 * and marks the list queries stale; reconnects resume with `Last-Event-ID`.
 */
import { useEffect } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { API_BASE, Notification } from '@algoboo/schema/api'
import { createSseParser } from './api/stream'
import { qk } from './api/keys'

/**
 * The notification stream's SSE connection, reconnecting with backoff from the last event id; returns its close. A
 * module function the hook's effect calls (lane BOO1): the React Compiler does not lower its loop and its `throw` in a try.
 */
function startNotificationStream(qc: QueryClient): () => void {
  const controller = new AbortController()
  let lastId: string | null = null
  let attempt = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  const seen = new Set<string>()
  const connect = async () => {
    try {
      const headers: Record<string, string> = { Accept: 'text/event-stream' }
      if (lastId) headers['Last-Event-ID'] = lastId
      const res = await fetch(`${API_BASE}/me/notifications/stream`, { headers, credentials: 'include', signal: controller.signal })
      // 401/403 (signed out) or 501 (not deployed): no point retrying in a loop.
      if (res.status >= 400 && res.status < 500) return
      if (!res.ok || !res.body) throw new Error(`notifications stream http ${res.status}`)
      attempt = 0
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      const parse = createSseParser()
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        for (const f of parse(decoder.decode(value, { stream: true }))) {
          if (f.id) lastId = f.id
          if (f.event !== 'notification') continue
          let item: Notification
          try {
            item = Notification.parse(JSON.parse(f.data))
          } catch {
            continue
          }
          if (seen.has(item.id)) continue
          seen.add(item.id)
          if (!item.readAt) qc.setQueryData<{ count: number }>(qk.unreadCount(), (c) => ({ count: (c?.count ?? 0) + 1 }))
          void qc.invalidateQueries({ queryKey: ['me', 'notifications', 'list'] })
        }
      }
      throw new Error('notifications stream closed')
    } catch {
      if (controller.signal.aborted) return
      attempt++
      timer = setTimeout(() => void connect(), Math.min(60_000, 2_000 * 2 ** Math.min(attempt - 1, 5)))
    }
  }
  void connect()
  return () => {
    controller.abort()
    if (timer) clearTimeout(timer)
  }
}

export function useNotificationStream(enabled: boolean): void {
  const qc = useQueryClient()
  useEffect(() => {
    if (!enabled || typeof fetch !== 'function' || typeof TextDecoder === 'undefined') return
    return startNotificationStream(qc)
  }, [enabled, qc])
}

/** The notification kinds of rooms: each leads to its room, at its message. */
export const ROOM_KINDS: ReadonlySet<string> = new Set(['room_mention', 'room_reply', 'room_invite', 'room_pin', 'room_dm', 'room_digest'])

/** Where a notification leads: its agent's manage page for owner kinds, the portfolio for trading kinds, a story or a challenge its own page. */
export function notificationHref(n: Pick<Notification, 'kind' | 'agent' | 'walletId'> & { payload?: Notification['payload'] }): string | null {
  const owner = ['executor_low_gas', 'agent_revision_proposed', 'token_launched', 'plan_lapsed'] as const
  // A day's story or a finished challenge names its own page (lane BOOUX1).
  const href = n.payload?.href
  if ((n.kind === 'daily_story' || n.kind === 'challenge_finished') && typeof href === 'string' && /^\/[a-z]/.test(href)) return href
  // A room notification names its message (lane CHATCORE1 `rooms.notify`: payload { roomId, seq, href }, lane CHATWEB1).
  if (ROOM_KINDS.has(n.kind)) {
    // a direct invite names its code (`/rooms/<id>?invite=<code>`): the invite page shows the room and joins with it
    const invite = typeof href === 'string' ? /^\/rooms\/[0-9a-f-]{36}\?invite=([A-Za-z0-9_-]{12,32})$/i.exec(href) : null
    if (invite) return `/rooms/invite/${invite[1]}`
    if (typeof href === 'string' && /^\/rooms\/[0-9a-f-]{36}(\?seq=\d+)?$/i.test(href)) return href
    const roomId = n.payload?.roomId
    const seq = n.payload?.seq
    if (typeof roomId === 'string' && /^[0-9a-f-]{36}$/i.test(roomId)) return typeof seq === 'number' && seq > 0 ? `/rooms/${roomId}?seq=${seq}` : `/rooms/${roomId}`
    return '/rooms'
  }
  if (n.kind === 'invoice_due' || n.kind === 'plan_lapsed' || n.kind === 'subscription_lapsed') return '/portfolio/billing'
  if (n.kind === 'reward_claimable') return '/portfolio'
  if (n.walletId && !(owner as readonly string[]).includes(n.kind)) return '/portfolio'
  if (n.agent) return (owner as readonly string[]).includes(n.kind) ? `/a/${n.agent.handle}/manage` : `/a/${n.agent.handle}`
  return null
}
